import { apiClient } from './apiClient';
import { Company, CreateCompanyRequest, UpdateCompanyRequest, CreateBankAccountRequest, UpdateBankAccountRequest, BankAccount, InvoiceNumberingSettings, InvoiceNumberingSettingsRequest } from '../types/company';

export const companyService = {
  // Получить все компании пользователя
  getCompanies: async (): Promise<Company[]> => {
    const response = await apiClient.get('/companies');
    return response.data;
  },

  // Получить компанию по ID
  getCompany: async (id: string): Promise<Company> => {
    const response = await apiClient.get(`/companies/${id}`);
    return response.data;
  },

  // Создать новую компанию
  createCompany: async (company: CreateCompanyRequest): Promise<Company> => {
    const response = await apiClient.post('/companies', company);
    return response.data;
  },

  // Обновить компанию
  updateCompany: async (id: string, company: UpdateCompanyRequest): Promise<Company> => {
    const response = await apiClient.put(`/companies/${id}`, company);
    return response.data;
  },

  // Удалить компанию
  deleteCompany: async (id: string): Promise<void> => {
    await apiClient.delete(`/companies/${id}`);
  },

  // Проверить существование банковского счета
  checkBankAccountExists: async (companyId: string, accountNumber: string): Promise<boolean> => {
    const response = await apiClient.get(`/companies/${companyId}/bank-accounts/check?accountNumber=${encodeURIComponent(accountNumber)}`);
    return response.data;
  },

  // Добавить банковский счет к компании
  addBankAccount: async (companyId: string, bankAccount: CreateBankAccountRequest): Promise<BankAccount> => {
    const response = await apiClient.post(`/companies/${companyId}/bank-accounts`, bankAccount);
    return response.data;
  },

  updateBankAccount: async (companyId: string, bankAccountId: string, bankAccount: UpdateBankAccountRequest): Promise<BankAccount> => {
    const response = await apiClient.put(`/companies/${companyId}/bank-accounts/${bankAccountId}`, bankAccount);
    return response.data;
  },

  deleteBankAccount: async (companyId: string, bankAccountId: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/bank-accounts/${bankAccountId}`);
  },

  // Настройки нумерации фактур
  getInvoiceNumberingSettings: async (companyId: string): Promise<InvoiceNumberingSettings> => {
    const response = await apiClient.get(`/companies/${companyId}/invoice-numbering-settings`);
    return response.data;
  },

  updateInvoiceNumberingSettings: async (companyId: string, settings: InvoiceNumberingSettingsRequest): Promise<InvoiceNumberingSettings> => {
    const response = await apiClient.put(`/companies/${companyId}/invoice-numbering-settings`, settings);
    return response.data;
  },

  generateInvoiceNumber: async (companyId: string, issueDate?: string): Promise<{ invoiceNumber: string }> => {
    const params = issueDate ? `?issueDate=${issueDate}` : '';
    const response = await apiClient.get(`/companies/${companyId}/invoices/generate-number${params}`);
    return response.data;
  },

  previewInvoiceNumber: async (companyId: string, issueDate?: string): Promise<{ invoiceNumber: string }> => {
    const params = issueDate ? `?issueDate=${issueDate}` : '';
    const response = await apiClient.get(`/companies/${companyId}/invoices/preview-number${params}`);
    return response.data;
  },

  // Настройки нумерации корректировочных счетов
  getCorrectionInvoiceNumberingSettings: async (companyId: string): Promise<InvoiceNumberingSettings> => {
    const response = await apiClient.get(`/companies/${companyId}/correction-invoice-numbering-settings`);
    return response.data;
  },

  updateCorrectionInvoiceNumberingSettings: async (companyId: string, settings: InvoiceNumberingSettingsRequest): Promise<InvoiceNumberingSettings> => {
    const response = await apiClient.put(`/companies/${companyId}/correction-invoice-numbering-settings`, settings);
    return response.data;
  },

  previewCorrectionInvoiceNumber: async (companyId: string, correctionDate?: string): Promise<{ correctionNumber: string }> => {
    const params = correctionDate ? `?correctionDate=${correctionDate}` : '';
    const response = await apiClient.post(`/companies/${companyId}/preview-correction-invoice-number${params}`);
    console.log('Preview correction invoice number response:', response.data);
    // Проверяем разные возможные форматы ответа
    const correctionNumber = response.data?.correctionNumber || response.data?.invoiceNumber || response.data;
    if (!correctionNumber) {
      console.error('No correction number in response:', response.data);
      throw new Error('No correction number in response');
    }
    return { correctionNumber: typeof correctionNumber === 'string' ? correctionNumber : String(correctionNumber) };
  }
};
